/**
===================================================
Module: frontend.src.contexts.AuthContext.tsx
Description: Authentication context provider for user management (Cognito + OTP)
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { User, LoginRequest, RegisterRequest, RegisterResponse, AuthContextType } from "../types";
import { apiService } from "../services/api";
import { NewPasswordRequiredError } from "../services/cognito";
import { setDeviceTrustToken } from "../utils/fingerprint";

interface OtpState {
  pending: boolean;
  maskedEmail: string;
  expiresInSeconds: number;
}

interface ExtendedAuthContextType extends AuthContextType {
  otpState: OtpState | null;
  sendOtp: () => Promise<void>;
  verifyOtp: (code: string) => Promise<boolean>;
  cancelOtp: () => void;
  /** True after a sign-in with a temporary password; the screen must
   *  collect a permanent one and call completeNewPassword. */
  passwordChangeRequired: boolean;
  completeNewPassword: (newPassword: string) => Promise<void>;
  cancelNewPassword: () => void;
}

const AuthContext = createContext<ExtendedAuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [otpState, setOtpState] = useState<OtpState | null>(null);
  const [pendingPasswordChange, setPendingPasswordChange] =
    useState<NewPasswordRequiredError | null>(null);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      if (apiService.isAuthenticated()) {
        try {
          // Import cognito service lazily to avoid circular dependency
          const { cognitoService } = await import("../services/cognito");

          // Validate session with Cognito
          const session = await cognitoService.getSession();
          if (session) {
            // Get user from backend
            const currentUser = await apiService.getCurrentUser();
            
            // Check if OTP is required (admin with OTP enabled, untrusted device)
            if (currentUser.otp_required) {
              setUser(currentUser);
              // Don't auto-send OTP on page load — let the component handle it
              setOtpState({
                pending: true,
                maskedEmail: currentUser.email ? 
                  (currentUser.email.length > 5 
                    ? currentUser.email.substring(0, 2) + "***@" + currentUser.email.split("@")[1]
                    : "***") 
                  : "***",
                expiresInSeconds: 600,
              });
            } else {
              setUser(currentUser);
            }
          } else {
            // Session invalid, clean up
            apiService.logout();
          }
        } catch (error) {
          console.error("Auth init error:", error);
          apiService.logout();
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Everything that happens once Cognito has issued tokens, whether they
  // came from a normal sign-in or from completing a new-password challenge.
  const finishSignIn = useCallback(async () => {
    try {
      const currentUser = await apiService.getCurrentUser();

      // Check if OTP is required
      if (currentUser.otp_required) {
        setUser(currentUser);
        // Auto-send OTP on login
        try {
          const otpResult = await apiService.sendOtp();
          if (otpResult.already_trusted) {
            // Device was already trusted, refresh user
            const refreshedUser = await apiService.getCurrentUser();
            setUser(refreshedUser);
            setOtpState(null);
          } else {
            setOtpState({
              pending: true,
              maskedEmail: otpResult.masked_email,
              expiresInSeconds: otpResult.expires_in_seconds,
            });
          }
        } catch (otpError) {
          console.error("Failed to send OTP:", otpError);
          setOtpState({
            pending: true,
            maskedEmail: currentUser.email || "***",
            expiresInSeconds: 600,
          });
        }
      } else {
        setUser(currentUser);
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      try {
        await apiService.login(credentials.email, credentials.password);
      } catch (error) {
        if (error instanceof NewPasswordRequiredError) {
          // Not a failure: the credentials were right, Cognito just needs a
          // permanent password before it will hand over tokens.
          setPendingPasswordChange(error);
          return;
        }
        throw error;
      }
      await finishSignIn();
    },
    [finishSignIn],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      if (!pendingPasswordChange) {
        throw new Error("No password change is pending");
      }
      await pendingPasswordChange.complete(newPassword);
      setPendingPasswordChange(null);
      // Same as the tail of apiService.login: make sure the backend row
      // exists before the profile is read. Failure there is not fatal.
      try {
        await apiService.syncUser();
      } catch (syncError) {
        console.warn("User sync failed:", syncError);
      }
      await finishSignIn();
    },
    [pendingPasswordChange, finishSignIn],
  );

  const cancelNewPassword = useCallback(() => {
    setPendingPasswordChange(null);
  }, []);

  const sendOtp = useCallback(async () => {
    try {
      const result = await apiService.sendOtp();
      if (result.already_trusted) {
        // Device is already trusted, refresh user to get is_superuser
        const refreshedUser = await apiService.getCurrentUser();
        setUser(refreshedUser);
        setOtpState(null);
      } else {
        setOtpState({
          pending: true,
          maskedEmail: result.masked_email,
          expiresInSeconds: result.expires_in_seconds,
        });
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const verifyOtp = useCallback(async (code: string): Promise<boolean> => {
    try {
      const result = await apiService.verifyOtp(code);
      if (result.verified) {
        // Store trust token
        setDeviceTrustToken(result.trust_token);
        setOtpState(null);
        // Refresh user to get is_superuser now that device is trusted
        const refreshedUser = await apiService.getCurrentUser();
        setUser(refreshedUser);
        return true;
      }
      return false;
    } catch (error) {
      throw error;
    }
  }, []);

  const cancelOtp = useCallback(() => {
    setOtpState(null);
    // Log out since OTP wasn't verified
    apiService.logout();
    setUser(null);
  }, []);

  const register = useCallback(async (data: RegisterRequest): Promise<RegisterResponse> => {
    try {
      const result = await apiService.register(
        data.email,
        data.password,
        data.firstName,
        data.lastName,
        data.phoneNumber,
        data.companyName,
        data.address
      );
      return result;
    } catch (error) {
      throw error;
    }
  }, []);

  const confirmRegistration = useCallback(async (email: string, code: string) => {
    await apiService.confirmRegistration(email, code);
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await apiService.forgotPassword(email);
  }, []);

  const resetPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    await apiService.resetPassword(email, code, newPassword);
  }, []);

  const logout = useCallback(() => {
    apiService.logout();
    // Don't clear device trust token — it should persist across logout/login
    // cycles so OTP isn't re-asked within the 24h trust window
    setUser(null);
    setOtpState(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (apiService.isAuthenticated()) {
      try {
        const currentUser = await apiService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error("Refresh user error:", error);
      }
    }
  }, []);

  const value: ExtendedAuthContextType = {
    user,
    isAuthenticated: !!user && !otpState?.pending,
    isLoading,
    login,
    register,
    confirmRegistration,
    forgotPassword,
    resetPassword,
    logout,
    refreshUser,
    otpState,
    sendOtp,
    verifyOtp,
    cancelOtp,
    passwordChangeRequired: !!pendingPasswordChange,
    completeNewPassword,
    cancelNewPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
